import React, { CSSProperties } from 'react';
import styled, { css } from 'styled-components';
import { useTestId } from '../../hooks/useTestId';
import { IThemePalette } from '../../Themes/interfaces';

export const loadingBarColours = (palette: IThemePalette) => ({
  loadingBar: { background: palette.primary.first[100], text: palette.neutral.white }
});

export interface IBaseBarProps {
  index: number;
  invertColours?: boolean;
}

const baseBarCss = css<IBaseBarProps>`
  background-color: ${({ theme, invertColours }) => (invertColours ? theme.colours.loadingBar.text : theme.colours.loadingBar.background)};
  width: 0.375rem;
  border-radius: 0.375rem;
  bottom: 0.375rem;
  align-self: end;
  animation: shoot 2s infinite ease-in-out ${({ index }) => (index + 1) * 0.2}s;

  @keyframes shoot {
    0% {
      height: 0;
      opacity: 0;
    }
    50% {
      height: 100%;
      opacity: 1;
    }
    100% {
      height: 0;
      opacity: 0;
    }
  }
`;

const BarBase = styled.span<IBaseBarProps>`
  ${baseBarCss}
`;

export interface ILoaderProps extends React.HTMLAttributes<HTMLSpanElement>, Pick<CSSProperties, 'height' | 'width'> {}

export const Loader = styled.span<ILoaderProps>`
  margin: 0.5rem 0;
  height: ${({ height }) => height};
  width: ${({ width }) => width};
  display: flex;
  justify-content: space-between;
`;

export interface ILoadingBarProps extends ILoaderProps, Partial<IBaseBarProps> {
  barCount?: number;
}

export const LoadingBar: React.FC<ILoadingBarProps> = ({ barCount = 6, height = '1.875rem', width = '5.625rem', invertColours = false, ...rest }) => {
  const { generateTestId } = useTestId(rest, 'LoadingBar');

  return (
    <Loader height={height} width={width} {...generateTestId('Loader')}>
      {[...Array.from({ length: barCount })].map((value, index) => (
        <BarBase key={index} index={index} invertColours={invertColours} {...generateTestId(`Bar${index}`)} />
      ))}
    </Loader>
  );
};
